import { getAppBase, resolveMediaUrl } from './styleHelpers.js';

export const isVideo = (url) => {
    if (typeof url !== 'string' || !url) return false;
    const cleanUrl = url.split('?')[0].split('#')[0].toLowerCase();
    return !!cleanUrl.match(/\.(mp4|webm|ogg|mov|m4v|mkv)$/i) || url.startsWith('data:video/');
};

export const getPageBackgroundStyle = (settings) => {
    if (!settings) return {};
    const isGradient = settings.pageBgMode === 'gradient';
    const grad = `linear-gradient(${settings.pageGradientAngle || '180'}deg, ${settings.pageGradientStart || settings.pageBackground || '#2a0a0a'}, ${settings.pageGradientEnd || settings.pageBackground || '#1a0505'})`;
    
    let bgImg = 'none';
    if (settings.pageBackgroundImage) {
        bgImg = `url('${settings.pageBackgroundImage}')`;
        if (isGradient) {
            bgImg = `${bgImg}, ${grad}`;
        }
    } else if (isGradient) {
        bgImg = grad;
    }

    return {
        backgroundColor: settings.pageBackground || 'transparent',
        backgroundImage: bgImg,
        backgroundSize: settings.pageBackgroundSize || 'cover',
        backgroundPosition: settings.pageBackgroundPosition || 'center',
        backgroundRepeat: settings.pageBackgroundRepeat || 'no-repeat'
    };
};

export const getEmbedUrl = (url, options = {}) => {
    if (!url || typeof url !== 'string') return null;
    const clean = url.trim();
    const isLoop = typeof options === 'boolean' ? options : (options?.loop !== undefined ? Boolean(options.loop) : true);
    const autoPlay = typeof options === 'object' && Boolean(options?.autoPlay);
    const mute = typeof options === 'object' && Boolean(options?.mute || (autoPlay && options?.mute !== false));

    // YouTube Shorts (e.g. youtube.com/shorts/XYZ)
    const ytShortsMatch = clean.match(/(?:youtube\.com\/shorts\/)([\w-]{11})/i);
    if (ytShortsMatch && ytShortsMatch[1]) {
        const id = ytShortsMatch[1];
        const params = [];
        if (isLoop) params.push(`loop=1&playlist=${id}`);
        if (autoPlay) params.push('autoplay=1');
        if (mute) params.push('mute=1');
        const query = params.length ? `?${params.join('&')}` : '';
        return `https://www.youtube.com/embed/${id}${query}`;
    }

    // YouTube (Standard, Embed, & youtu.be)
    const ytMatch = clean.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/i);
    if (ytMatch && ytMatch[1]) {
        const id = ytMatch[1];
        const params = [];
        if (isLoop) params.push(`loop=1&playlist=${id}`);
        if (autoPlay) params.push('autoplay=1');
        if (mute) params.push('mute=1');
        const query = params.length ? `?${params.join('&')}` : '';
        return `https://www.youtube.com/embed/${id}${query}`;
    }

    // Vimeo (Standard, Player, Channels)
    const vimeoMatch = clean.match(/(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/[^\/]*\/videos\/|video\/|))(\d+)/i);
    if (vimeoMatch && vimeoMatch[1]) {
        const id = vimeoMatch[1];
        const params = [];
        if (isLoop) params.push('loop=1');
        if (autoPlay) params.push('autoplay=1');
        if (mute) params.push('muted=1');
        const query = params.length ? `?${params.join('&')}` : '';
        return `https://player.vimeo.com/video/${id}${query}`;
    }

    return null;
};

export const getNestedNavLinks = (links) => {
    const nested = [];
    let currentParent = null;
    (links || []).forEach(link => {
        if (link.indent) {
            if (currentParent) {
                currentParent.children = currentParent.children || [];
                currentParent.children.push(link);
            } else {
                currentParent = { ...link, children: [] };
                nested.push(currentParent);
            }
        } else {
            currentParent = { ...link, children: [] };
            nested.push(currentParent);
        }
    });
    return nested;
};

export const isObject = (item) => {
    return (item && typeof item === 'object' && !Array.isArray(item));
};

export const resolveImageUrl = (url) => {
    if (!url || typeof url !== 'string') return '/hero_abstract_bg.png';
    const trimmed = url.trim();
    if (!trimmed) return '/hero_abstract_bg.png';

    // If url contains product page route (e.g. /product/prod_123 or product/123), it's a link, NOT an image!
    if (trimmed.includes('/product/') || trimmed.startsWith('product/')) {
        return '/hero_abstract_bg.png';
    }

    const res = resolveMediaUrl(trimmed);
    return res || '/hero_abstract_bg.png';
};

export const getProductImageUrl = (product, fallback = '/hero_abstract_bg.png') => {
    if (!product) return resolveImageUrl(fallback);

    // Normalize images & gallery if stored as stringified JSON
    let imgs = product.images;
    if (typeof imgs === 'string' && imgs.trim().startsWith('[')) {
        try { imgs = JSON.parse(imgs); } catch(e) {}
    }

    let gallery = product.gallery;
    if (typeof gallery === 'string' && gallery.trim().startsWith('[')) {
        try { gallery = JSON.parse(gallery); } catch(e) {}
    }

    // 1. Check singular image string
    if (typeof product.image === 'string' && product.image.trim()) {
        return resolveImageUrl(product.image);
    }

    // 2. Check images array
    if (Array.isArray(imgs) && imgs.length > 0) {
        for (const img of imgs) {
            if (typeof img === 'string' && img.trim()) {
                return resolveImageUrl(img);
            }
            if (img && typeof img === 'object') {
                if (typeof img.src === 'string' && img.src.trim()) return resolveImageUrl(img.src);
                if (typeof img.url === 'string' && img.url.trim()) return resolveImageUrl(img.url);
            }
        }
    }

    // 3. Check gallery array
    if (Array.isArray(gallery) && gallery.length > 0) {
        for (const img of gallery) {
            if (typeof img === 'string' && img.trim()) {
                return resolveImageUrl(img);
            }
            if (img && typeof img === 'object') {
                if (typeof img.src === 'string' && img.src.trim()) return resolveImageUrl(img.src);
                if (typeof img.url === 'string' && img.url.trim()) return resolveImageUrl(img.url);
            }
        }
    }

    // 4. Check variations array for variation images
    let variations = product.variations;
    if (typeof variations === 'string' && variations.trim().startsWith('[')) {
        try { variations = JSON.parse(variations); } catch(e) {}
    }
    if (Array.isArray(variations) && variations.length > 0) {
        for (const v of variations) {
            if (v && typeof v.image === 'string' && v.image.trim()) {
                return resolveImageUrl(v.image);
            }
        }
    }

    return resolveImageUrl(fallback);
};

export const formatHtmlDescription = (str) => {
    if (!str || typeof str !== 'string') return '';
    let text = str.trim();
    if (!text) return '';

    // Normalize literal escape sequences (\r\n, \n, \r)
    text = text.replace(/\\r\\n/g, '\n').replace(/\\r/g, '\n').replace(/\\n/g, '\n');

    // Remove CDATA wrappers
    text = text.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/gi, '$1');

    // Check if HTML block tags exist (<p>, <div>, <h4>, <h3>, <ul>, <ol>, <li>, <table>, <section>, <article>, <br>)
    const hasBlockTags = /<(?:p|div|h[1-6]|ul|ol|li|table|blockquote|section|article|br)[^>]*>/i.test(text);

    if (!hasBlockTags) {
        text = text
            .split(/\n\s*\n/)
            .map(para => {
                const trimmed = para.trim();
                return trimmed ? `<p>${trimmed.replace(/\n/g, '<br />')}</p>` : '';
            })
            .filter(Boolean)
            .join('\n');
    }

    return text;
};

export const cleanHtmlDescription = (str) => {
    if (!str || typeof str !== 'string') return '';

    let text = str;

    // Normalise literal escape sequences that WooCommerce CSVs and PHP-serialized
    // WordPress data store as two-character strings (backslash + letter) rather
    // than real control characters. Must be done BEFORE HTML processing.
    // Replace literal \r\n first, then standalone \r and \n.
    text = text.replace(/\\r\\n/g, '\n');
    text = text.replace(/\\r/g, '\n');
    text = text.replace(/\\n/g, '\n');

    // Remove CDATA wrappers
    text = text.replace(/<!\[CDATA\[([\s\S]*?)\]\]>/gi, '$1');

    // Remove HTML comments and WordPress Gutenberg block comments (<!-- wp:paragraph --> etc.)
    text = text.replace(/<!--[\s\S]*?-->/g, '');

    // Remove WordPress shortcodes e.g. [gallery ids="1,2"], [vc_row], [/vc_row], [vc_column_text]
    text = text.replace(/\[\/?[\w-_]+(?:\s+[^\]]*)?\]/gi, '');

    // Convert table cells/rows
    text = text
        .replace(/<t[hd][^>]*>/gi, ' ')
        .replace(/<\/t[hd]>/gi, '')
        .replace(/<\/tr>/gi, '\n');

    // Convert block-level HTML tags to newlines before stripping tags
    text = text
        .replace(/<\/(p|div|h[1-6]|table|section|article|blockquote)>/gi, '\n\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<\/li>/gi, '\n')
        .replace(/<li[^>]*>/gi, '• ');

    // Strip remaining HTML tags
    text = text.replace(/<[^>]*>?/gm, '');

    // Decode HTML entities (named & numeric)
    text = text
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&#8217;/g, "'")
        .replace(/&#8216;/g, "'")
        .replace(/&#8220;/g, '"')
        .replace(/&#8221;/g, '"')
        .replace(/&#8211;/g, '–')
        .replace(/&#8212;/g, '—')
        .replace(/&nbsp;/g, ' ')
        .replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec))
        .replace(/&#x([0-9a-f]+);/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16)));

    // Collapse 3+ consecutive newlines down to 2 (one blank line max)
    text = text.replace(/\n{3,}/g, '\n\n');

    // Clean up: trim each line, then remove leading/trailing blank lines
    const lines = text.split('\n').map(line => line.trim());

    // Drop leading blank lines
    while (lines.length > 0 && lines[0] === '') lines.shift();
    // Drop trailing blank lines
    while (lines.length > 0 && lines[lines.length - 1] === '') lines.pop();

    // Collapse mid-string runs of more than one consecutive blank line to one
    const collapsed = [];
    let prevBlank = false;
    for (const line of lines) {
        if (line === '') {
            if (!prevBlank) collapsed.push(line);
            prevBlank = true;
        } else {
            collapsed.push(line);
            prevBlank = false;
        }
    }

    return collapsed.join('\n');
};

export const sanitizeInputString = (str) => {
    if (typeof str !== 'string') return str;
    return str
        .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '[Security Shield: Script Removed]')
        .replace(/javascript\s*:/gi, 'blocked-scheme:')
        .replace(/on\w+\s*=/gi, 'data-blocked-event=');
};

export const sanitizeConfigData = (obj) => {
    if (typeof obj === 'string') return sanitizeInputString(obj);
    if (Array.isArray(obj)) return obj.map(sanitizeConfigData);
    if (obj !== null && typeof obj === 'object') {
        const cleaned = {};
        for (const [key, val] of Object.entries(obj)) {
            cleaned[key] = sanitizeConfigData(val);
        }
        return cleaned;
    }
    return obj;
};

export const slugify = (text) => {
    if (typeof text !== 'string') return '';
    return text
        .toString()
        .replace(/<[^>]*>/g, '')
        .toLowerCase()
        .trim()
        .replace(/\s+/g, '-')
        .replace(/[^\w-]+/g, '')
        .replace(/--+/g, '-')
        .replace(/^-+/, '')
        .replace(/-+$/, '');
};

export const normalizeSlug = (s) => {
    if (!s) return '';
    try {
        s = decodeURIComponent(String(s));
    } catch (e) {}
    return String(s)
        .trim()
        .toLowerCase()
        .replace(/^\/+|\/+$/g, '')
        .replace(/[\s_]+/g, '-')
        .replace(/[^a-z0-9\-]/g, '')
        .replace(/-+/g, '-');
};

export const extractSlugFromUrl = (url) => {
    if (typeof url !== 'string' || !url) return '';
    try {
        const clean = url.split('?')[0].split('#')[0].replace(/\/+$/, '');
        const segments = clean.split('/').filter(Boolean);
        return segments.length > 0 ? segments[segments.length - 1] : '';
    } catch (e) {
        return '';
    }
};

/**
 * Standardized category and subcategory string / slug matcher
 */
export const isCategoryMatch = (productCat, targetCat) => {
    if (!targetCat || targetCat === 'All') return true;
    if (!productCat) return false;
    if (productCat === targetCat) return true;
    const normProduct = String(productCat).toLowerCase().trim();
    const normTarget = String(targetCat).toLowerCase().trim();
    if (normProduct === normTarget) return true;
    if (slugify(normProduct) === slugify(normTarget)) return true;
    return false;
};

export const isSubCategoryMatch = (productSubCat, targetSubCat) => {
    if (!targetSubCat || targetSubCat === 'All') return true;
    if (!productSubCat) return false;
    if (productSubCat === targetSubCat) return true;
    const normProduct = String(productSubCat).toLowerCase().trim();
    const normTarget = String(targetSubCat).toLowerCase().trim();
    if (normProduct === normTarget) return true;
    if (slugify(normProduct) === slugify(normTarget)) return true;
    return false;
};

/**
 * Extract all categories and subcategories associated with a product across string, array, or object formats
 */
export const getProductCategoriesList = (p) => {
    if (!p) return [];
    const cats = [];
    if (Array.isArray(p.categories)) {
        p.categories.forEach(c => {
            if (typeof c === 'string') cats.push(c);
            else if (c && typeof c === 'object' && c.name) cats.push(c.name);
        });
    }
    if (typeof p.category === 'string') {
        if (p.category.includes(',')) {
            p.category.split(',').forEach(c => cats.push(c.trim()));
        } else {
            cats.push(p.category);
        }
    } else if (p.category && typeof p.category === 'object' && p.category.name) {
        cats.push(p.category.name);
    }
    if (typeof p.subCategory === 'string') {
        if (p.subCategory.includes(',')) {
            p.subCategory.split(',').forEach(c => cats.push(c.trim()));
        } else {
            cats.push(p.subCategory);
        }
    } else if (p.subCategory && typeof p.subCategory === 'object' && p.subCategory.name) {
        cats.push(p.subCategory.name);
    }
    return cats.filter(Boolean);
};

export const isProductMatchingCategory = (p, cat, subCatsOfTarget = []) => {
    if (!cat || cat === 'All') return true;
    const cats = getProductCategoriesList(p);
    if (cats.length === 0) return false;
    if (cats.some(c => isCategoryMatch(c, cat))) return true;
    if (subCatsOfTarget.length > 0 && cats.some(c => subCatsOfTarget.some(sub => isCategoryMatch(c, sub.name)))) {
        return true;
    }
    return false;
};

export const isProductMatchingSubCategory = (p, targetSubCat) => {
    if (!targetSubCat || targetSubCat === 'All') return true;
    const cats = getProductCategoriesList(p);
    if (cats.length === 0) return false;
    return cats.some(c => isSubCategoryMatch(c, targetSubCat));
};

/**
 * Extract non-zero numerical prices from a product and its variations.
 */
export const getProductPricesList = (product) => {
    if (!product) return [];
    const prices = new Set();

    const parseVal = (val) => {
        if (val === undefined || val === null || val === '') return null;
        const num = parseFloat(String(val).replace(/[^0-9.]/g, ''));
        return (!isNaN(num) && num > 0) ? num : null;
    };

    const topPrice = parseVal(product.price);
    if (topPrice !== null) prices.add(topPrice);

    const vars = product.variations || product.pageConfig?.variations;
    if (Array.isArray(vars)) {
        vars.forEach(v => {
            if (!v) return;
            const vp = parseVal(v.price ?? v.salePrice ?? v.regularPrice);
            if (vp !== null) prices.add(vp);
        });
    }

    return Array.from(prices).sort((a, b) => a - b);
};

export const getMinProductPrice = (product) => {
    const prices = getProductPricesList(product);
    if (prices.length > 0) return prices[0];
    const rawPrice = parseFloat(String(product?.price || 0));
    return isNaN(rawPrice) ? 0 : rawPrice;
};

export const getProductDisplayPrice = (product, customFormatter, customCallForPrice) => {
    if (!product) return '$0.00';
    if (product.inquiryOnly) return (customCallForPrice || 'Call for Price');

    const fmt = typeof customFormatter === 'function' ? customFormatter : (val) => `$${parseFloat(val || 0).toFixed(2)}`;
    const prices = getProductPricesList(product);

    if (prices.length === 0) {
        const rawPrice = parseFloat(String(product.price || 0));
        return (isNaN(rawPrice) || rawPrice <= 0) ? fmt(0) : fmt(rawPrice);
    }

    if (prices.length === 1) {
        return fmt(prices[0]);
    }

    const minVal = prices[0];
    const maxVal = prices[prices.length - 1];
    if (minVal === maxVal) return fmt(minVal);

    return `${fmt(minVal)} – ${fmt(maxVal)}`;
};

/**
 * Normalizes an attribute key for flexible comparison across platforms (WooCommerce, Shopify, CSV, JSON):
 * e.g., 'attribute_pa_color' -> 'color'
 *       'pa_color' -> 'color'
 *       'T-Shirt Size' -> 'tshirtsize'
 */
export const normalizeAttributeKey = (key) => {
    if (!key || typeof key !== 'string') return '';
    return key
        .toLowerCase()
        .replace(/^attribute_pa_|^attribute_|^pa_/, '')
        .replace(/[^a-z0-9]/g, '');
};

/**
 * Normalizes an attribute value for flexible matching:
 * e.g., 'Dark Blue' -> 'darkblue'
 *       'dark-blue' -> 'darkblue'
 *       '12 V' -> '12v'
 */
export const normalizeAttributeValue = (val) => {
    if (val === undefined || val === null) return '';
    if (typeof val === 'object') {
        const extracted = val.name || val.option || val.slug || val.value || val.title || '';
        return String(extracted).toLowerCase().replace(/[^a-z0-9]/g, '');
    }
    return String(val).toLowerCase().replace(/[^a-z0-9]/g, '');
};

/**
 * Extracts a normalized map { normalizedKey: rawValue } from any variation object or array representation.
 */
export const getVariationAttributesMap = (variation) => {
    if (!variation) return {};
    const attrs = variation.attributes || variation.pageConfig?.attributes || {};
    const map = {};

    if (Array.isArray(attrs)) {
        attrs.forEach(a => {
            if (!a) return;
            const name = a.name || a.key || a.attribute || a.title || '';
            const val = a.option !== undefined ? a.option : (a.value !== undefined ? a.value : (a.slug || ''));
            const normKey = normalizeAttributeKey(name);
            if (normKey) {
                map[normKey] = val;
            }
        });
    } else if (typeof attrs === 'object') {
        Object.entries(attrs).forEach(([k, v]) => {
            const normKey = normalizeAttributeKey(k);
            if (normKey) {
                map[normKey] = v;
            }
        });
    }

    // Also check direct variation properties like option1, option2, color, size
    ['option1', 'option2', 'option3', 'color', 'size', 'material', 'style', 'type'].forEach(k => {
        if (variation[k] !== undefined && variation[k] !== null) {
            const normKey = normalizeAttributeKey(k);
            if (normKey && map[normKey] === undefined) {
                map[normKey] = variation[k];
            }
        }
    });

    return map;
};

/**
 * Extracts all attributes and options from a product, combining top-level definitions and variations.
 */
export const extractProductAttributes = (product) => {
    if (!product) return {};
    const rawAttrs = (product.attributes && Object.keys(product.attributes).length > 0)
        ? product.attributes
        : (product.pageConfig?.attributes || {});

    const rawVars = Array.isArray(product.variations) && product.variations.length > 0
        ? product.variations
        : (Array.isArray(product.pageConfig?.variations) ? product.pageConfig.variations : []);

    const result = {};

    // 1. Populate from rawAttrs
    Object.entries(rawAttrs).forEach(([k, v]) => {
        if (!k) return;
        if (Array.isArray(v)) {
            const opts = v.map(opt => typeof opt === 'object' ? (opt.name || opt.option || opt.slug || JSON.stringify(opt)) : String(opt).trim()).filter(Boolean);
            if (opts.length > 0) result[k] = Array.from(new Set(opts));
        } else if (typeof v === 'string' && v.trim()) {
            if (v.includes(',') || v.includes('|')) {
                const opts = v.split(/[,|]/).map(s => s.trim()).filter(Boolean);
                if (opts.length > 0) result[k] = Array.from(new Set(opts));
            } else {
                result[k] = [v.trim()];
            }
        }
    });

    // 2. Harvest attributes from variations
    if (rawVars.length > 0) {
        rawVars.forEach(v => {
            if (!v) return;
            const rawVAttrs = v.attributes || {};
            
            if (Array.isArray(rawVAttrs)) {
                rawVAttrs.forEach(a => {
                    if (!a) return;
                    const name = a.name || a.key || a.attribute || '';
                    const val = a.option !== undefined ? a.option : (a.value !== undefined ? a.value : (a.slug || ''));
                    if (name && val) {
                        const existingKey = Object.keys(result).find(k => normalizeAttributeKey(k) === normalizeAttributeKey(name)) || name;
                        if (!result[existingKey]) result[existingKey] = [];
                        const strVal = String(val).trim();
                        if (strVal && !result[existingKey].includes(strVal)) {
                            result[existingKey].push(strVal);
                        }
                    }
                });
            } else if (typeof rawVAttrs === 'object') {
                Object.entries(rawVAttrs).forEach(([vk, vv]) => {
                    if (vk && vv !== undefined && vv !== null && vv !== '') {
                        const existingKey = Object.keys(result).find(k => normalizeAttributeKey(k) === normalizeAttributeKey(vk)) || vk;
                        if (!result[existingKey]) result[existingKey] = [];
                        const strVal = String(vv).trim();
                        if (strVal && !result[existingKey].includes(strVal)) {
                            result[existingKey].push(strVal);
                        }
                    }
                });
            }
        });
    }

    return result;
};

/**
 * Finds the matching variation given a product and user's selected options { Color: 'Red', Size: 'M' }
 */
export const findMatchingVariation = (product, selectedOptions = {}) => {
    if (!product) return null;
    const rawVars = Array.isArray(product.variations) && product.variations.length > 0
        ? product.variations
        : (Array.isArray(product.pageConfig?.variations) ? product.pageConfig.variations : []);

    if (!rawVars || rawVars.length === 0) return null;
    if (!selectedOptions || Object.keys(selectedOptions).length === 0) {
        return rawVars[0] || null;
    }

    const selectedNormalized = {};
    Object.entries(selectedOptions).forEach(([k, v]) => {
        const normKey = normalizeAttributeKey(k);
        if (normKey) {
            selectedNormalized[normKey] = normalizeAttributeValue(v);
        }
    });

    const selectedKeys = Object.keys(selectedNormalized);
    if (selectedKeys.length === 0) return rawVars[0] || null;

    let bestMatch = null;
    let maxMatchScore = -1;

    for (const v of rawVars) {
        if (!v) continue;
        const vAttrMap = getVariationAttributesMap(v);
        const vKeys = Object.keys(vAttrMap);

        let matchesAll = true;
        let matchCount = 0;

        for (const sKey of selectedKeys) {
            const sVal = selectedNormalized[sKey];
            if (!sVal) continue;

            const vValRaw = vAttrMap[sKey];
            if (vValRaw === undefined) {
                // Variation doesn't specify this attribute (wildcard for this attribute)
                continue;
            }

            const vValNorm = normalizeAttributeValue(vValRaw);
            // Wildcard match for 'any', 'all', empty string, or exact match
            if (vValNorm === '' || vValNorm === 'any' || vValNorm === 'all' || vValNorm === sVal) {
                if (vValNorm === sVal) {
                    matchCount += 2; // Higher weight for exact value match
                } else {
                    matchCount += 1;
                }
            } else {
                matchesAll = false;
                break;
            }
        }

        if (matchesAll) {
            // Also check that all attributes explicitly specified on the variation match the selected options
            for (const vKey of vKeys) {
                const vValNorm = normalizeAttributeValue(vAttrMap[vKey]);
                if (vValNorm && vValNorm !== 'any' && vValNorm !== 'all') {
                    const sVal = selectedNormalized[vKey];
                    if (sVal !== undefined && sVal !== vValNorm) {
                        matchesAll = false;
                        break;
                    }
                }
            }
        }

        if (matchesAll && matchCount > maxMatchScore) {
            maxMatchScore = matchCount;
            bestMatch = v;
        }
    }

    return bestMatch || rawVars[0] || null;
};

export const PLACEHOLDERS = [
    'Add an eye-catching headline...',
    'Share a compelling subtitle or description here...',
    'Your Company Name',
    'Describe your amazing feature or service here.',
    'Click to edit heading',
    'Click to edit text',
    'Peaks at sunrise.',
    'Lush canopy.'
];

export const isPlaceholderText = (text) => {
    if (!text || typeof text !== 'string') return false;
    const clean = text.trim().toLowerCase();
    return PLACEHOLDERS.some(p => p.toLowerCase() === clean);
};

export const getProductLinks = (product) => {
    if (!product) return [];
    const list = [];

    // 1. Primary spec sheet link
    const specUrl = product.specSheetUrl || product.pageConfig?.specSheetUrl;
    const specTitle = product.specSheetTitle || product.specSheetLabel || product.pageConfig?.specSheetTitle || product.pageConfig?.specSheetLabel;

    if (specUrl) {
        list.push({
            id: 'specSheetPrimary',
            title: specTitle || 'Download Technical Spec Sheet (PDF)',
            url: specUrl,
            icon: '📄',
            isPrimary: true
        });
    }

    // 2. Additional custom links array
    const rawCustomLinks = product.customLinks || product.pageConfig?.customLinks || product.additionalLinks || [];
    if (Array.isArray(rawCustomLinks)) {
        rawCustomLinks.forEach((item, idx) => {
            if (!item) return;
            const url = typeof item === 'string' ? item : item.url;
            if (!url) return;
            const title = (typeof item === 'object' && (item.title || item.label || item.name)) || `Document Link ${idx + 1}`;
            const icon = (typeof item === 'object' && item.icon) || '📎';
            list.push({
                id: (typeof item === 'object' && item.id) ? item.id : `custom-link-${idx}`,
                title: title,
                url: url,
                icon: icon,
                isPrimary: false
            });
        });
    }

    return list;
};




